import { openMeldRuntimeProfileIdSchema } from "../protocol/profile.js";
import * as z$1 from "zod/v4";
//#region src/schemas/http/space-tasks.ts
const SPACE_TASK_TITLE_MAX_CHARS = 200;
const SPACE_TASK_DESCRIPTION_MAX_CHARS = 8e3;
const SPACE_TASK_COMPLETION_CONDITION_MAX_CHARS = 500;
const SPACE_TASK_COMPLETION_CONDITION_MAX_ITEMS = 12;
const SPACE_TASK_EXTERNAL_WORK_ITEM_MAX_ITEMS = 12;
const SPACE_TASK_RESULT_SUMMARY_MAX_CHARS = 8e3;
const SPACE_TASK_REVIEW_FEEDBACK_MAX_CHARS = 8e3;
const SPACE_TASK_OPERATION_ID_MAX_CHARS = 200;
const SPACE_TASK_LIST_MAX_ITEMS = 500;
const optionalTrimmedText = (max) => z$1.union([z$1.string().trim().min(1).max(max), z$1.null()]);
const spaceTaskIdSchema = z$1.string().trim().regex(/^tsk_[0-9a-f-]{36}$/u);
const spaceTaskPhaseSchema = z$1.enum([
	"to_do",
	"in_progress",
	"in_review",
	"done",
	"canceled"
]);
const spaceTaskActorSchema = z$1.strictObject({
	profileId: openMeldRuntimeProfileIdSchema,
	profileKind: z$1.enum(["human", "agent"]),
	profileName: z$1.string().min(1).max(200),
	ownerUserId: z$1.string().min(1).max(512).nullable()
});
const spaceTaskResultSchema = z$1.strictObject({
	resultRevision: z$1.number().int().min(1),
	summary: z$1.string().trim().min(1).max(SPACE_TASK_RESULT_SUMMARY_MAX_CHARS),
	submittedAt: z$1.string().datetime(),
	submittedBy: spaceTaskActorSchema
});
const spaceTaskReviewSchema = z$1.strictObject({
	decision: z$1.enum(["accepted", "changes_requested"]),
	feedback: optionalTrimmedText(SPACE_TASK_REVIEW_FEEDBACK_MAX_CHARS),
	resultRevision: z$1.number().int().min(1),
	reviewedAt: z$1.string().datetime(),
	reviewedBy: spaceTaskActorSchema
});
const spaceTaskEventTypeSchema = z$1.enum([
	"created",
	"assigned",
	"brief_updated",
	"reviewer_set",
	"marked_in_progress",
	"result_submitted",
	"review_accepted",
	"changes_requested",
	"canceled",
	"reopened",
	"rework_continued",
	"external_work_item_attached",
	"external_work_item_detached",
	"external_work_item_observed"
]);
const spaceTaskExternalWorkItemProviderSchema = z$1.literal("github");
const spaceTaskExternalWorkItemObservedStateSchema = z$1.enum(["open", "closed"]);
const spaceTaskExternalWorkItemSchema = z$1.strictObject({
	lastObservedAt: z$1.string().datetime().nullable(),
	lastObservedState: z$1.union([spaceTaskExternalWorkItemObservedStateSchema, z$1.null()]),
	number: z$1.number().int().positive(),
	owner: z$1.string().trim().min(1).max(100),
	provider: spaceTaskExternalWorkItemProviderSchema,
	repo: z$1.string().trim().min(1).max(100),
	url: z$1.url().max(2048)
});
function spaceTaskExternalWorkItemIdentityKey(item) {
	return `${item.provider}:${item.owner.toLowerCase()}/${item.repo.toLowerCase()}#${item.number}`;
}
function formatSpaceTaskGitHubIssueLabel(item) {
	return `${item.owner}/${item.repo}#${item.number}`;
}
function spaceTaskGitHubIssueOperationId(kind, identity, taskId) {
	const base = `github-issue-${kind}:${identity.owner.toLowerCase()}:${identity.repo.toLowerCase()}:${identity.number}`;
	return taskId ? `${base}:${taskId}` : base;
}
function describeLinkedGitHubIssueClosePrompt(task) {
	if (task.phase !== "done" && task.phase !== "canceled") return null;
	const openIssues = task.externalWorkItems.filter((item) => item.provider === "github" && item.lastObservedState !== "closed");
	if (openIssues.length === 0) return null;
	const labels = openIssues.map((item) => formatSpaceTaskGitHubIssueLabel(item));
	if (labels.length === 1) return `This Task is linked to GitHub issue ${labels[0]}. Close that issue in GitHub if the work there is finished.`;
	return `This Task is linked to GitHub issues ${labels.join(", ")}. Close those issues in GitHub if the work there is finished.`;
}
const spaceTaskEventSchema = z$1.strictObject({
	eventId: z$1.string().trim().regex(/^tse_[0-9a-f-]{36}$/u),
	taskId: spaceTaskIdSchema,
	operationId: z$1.string().trim().min(1).max(200),
	type: spaceTaskEventTypeSchema,
	actor: spaceTaskActorSchema,
	fromPhase: z$1.union([spaceTaskPhaseSchema, z$1.null()]),
	toPhase: spaceTaskPhaseSchema,
	taskRevision: z$1.number().int().min(1),
	detail: optionalTrimmedText(SPACE_TASK_RESULT_SUMMARY_MAX_CHARS),
	createdAt: z$1.string().datetime()
});
const spaceTaskSchema = z$1.strictObject({
	taskId: spaceTaskIdSchema,
	spaceId: z$1.string().trim().min(1).max(512),
	title: z$1.string().trim().min(1).max(200),
	description: optionalTrimmedText(SPACE_TASK_DESCRIPTION_MAX_CHARS),
	completionConditions: z$1.array(z$1.string().trim().min(1).max(500)).max(12),
	sourceSignalId: z$1.string().trim().min(1).max(512).nullable(),
	threadId: z$1.string().trim().min(1).max(512).nullable(),
	requestedBy: spaceTaskActorSchema,
	createdBy: spaceTaskActorSchema,
	assignee: z$1.union([spaceTaskActorSchema, z$1.null()]),
	reviewer: z$1.union([spaceTaskActorSchema, z$1.null()]),
	phase: spaceTaskPhaseSchema,
	result: z$1.union([spaceTaskResultSchema, z$1.null()]),
	review: z$1.union([spaceTaskReviewSchema, z$1.null()]),
	automaticReworkRounds: z$1.number().int().min(0).max(2),
	needsHumanDecision: z$1.boolean(),
	externalWorkItems: z$1.array(spaceTaskExternalWorkItemSchema).max(12).default([]),
	revision: z$1.number().int().min(1),
	createdAt: z$1.string().datetime(),
	updatedAt: z$1.string().datetime()
});
const operationIdSchema = z$1.string().trim().min(1).max(200);
const expectedRevisionSchema = z$1.number().int().min(1);
const createSpaceTaskBodySchema = z$1.strictObject({
	operationId: operationIdSchema,
	title: spaceTaskSchema.shape.title,
	description: spaceTaskSchema.shape.description.optional(),
	completionConditions: spaceTaskSchema.shape.completionConditions.optional(),
	sourceSignalId: spaceTaskSchema.shape.sourceSignalId.optional(),
	threadId: spaceTaskSchema.shape.threadId.optional(),
	assigneeProfileId: openMeldRuntimeProfileIdSchema.nullable().optional(),
	reviewerProfileId: openMeldRuntimeProfileIdSchema.nullable().optional(),
	externalWorkItems: spaceTaskSchema.shape.externalWorkItems.optional()
}).superRefine((value, ctx) => {
	if (value.assigneeProfileId && value.reviewerProfileId === value.assigneeProfileId) ctx.addIssue({
		code: "custom",
		path: ["reviewerProfileId"],
		message: "An independent Reviewer must differ from the assignee"
	});
	const items = value.externalWorkItems ?? [];
	const seen = /* @__PURE__ */ new Set();
	for (const [index, item] of items.entries()) {
		const key = spaceTaskExternalWorkItemIdentityKey(item);
		if (seen.has(key)) {
			ctx.addIssue({
				code: "custom",
				path: ["externalWorkItems", index],
				message: "Each linked GitHub issue can appear only once on a Task"
			});
			continue;
		}
		seen.add(key);
	}
});
const taskMutationBase = {
	operationId: operationIdSchema,
	expectedRevision: expectedRevisionSchema
};
const mutateSpaceTaskBodySchema = z$1.discriminatedUnion("action", [
	z$1.strictObject({
		...taskMutationBase,
		action: z$1.literal("assign"),
		assigneeProfileId: openMeldRuntimeProfileIdSchema.nullable()
	}),
	z$1.strictObject({
		...taskMutationBase,
		action: z$1.literal("update_brief"),
		title: spaceTaskSchema.shape.title.optional(),
		description: spaceTaskSchema.shape.description.optional(),
		completionConditions: spaceTaskSchema.shape.completionConditions.optional()
	}),
	z$1.strictObject({
		...taskMutationBase,
		action: z$1.literal("set_reviewer"),
		reviewerProfileId: openMeldRuntimeProfileIdSchema.nullable()
	}),
	z$1.strictObject({
		...taskMutationBase,
		action: z$1.literal("mark_in_progress")
	}),
	z$1.strictObject({
		...taskMutationBase,
		action: z$1.literal("submit_result"),
		summary: spaceTaskResultSchema.shape.summary
	}),
	z$1.strictObject({
		...taskMutationBase,
		action: z$1.literal("review"),
		decision: spaceTaskReviewSchema.shape.decision,
		feedback: spaceTaskReviewSchema.shape.feedback.optional()
	}),
	z$1.strictObject({
		...taskMutationBase,
		action: z$1.literal("cancel"),
		reason: z$1.string().trim().min(1).max(1e3)
	}),
	z$1.strictObject({
		...taskMutationBase,
		action: z$1.literal("reopen"),
		reason: z$1.string().trim().min(1).max(1e3)
	}),
	z$1.strictObject({
		...taskMutationBase,
		action: z$1.literal("continue_rework"),
		reason: z$1.string().trim().min(1).max(1e3)
	}),
	z$1.strictObject({
		...taskMutationBase,
		action: z$1.literal("attach_external_work_item"),
		item: spaceTaskExternalWorkItemSchema
	}),
	z$1.strictObject({
		...taskMutationBase,
		action: z$1.literal("detach_external_work_item"),
		number: spaceTaskExternalWorkItemSchema.shape.number,
		owner: spaceTaskExternalWorkItemSchema.shape.owner,
		provider: spaceTaskExternalWorkItemProviderSchema,
		repo: spaceTaskExternalWorkItemSchema.shape.repo
	}),
	z$1.strictObject({
		...taskMutationBase,
		action: z$1.literal("observe_external_work_item"),
		lastObservedAt: z$1.string().datetime().optional(),
		lastObservedState: spaceTaskExternalWorkItemObservedStateSchema,
		number: spaceTaskExternalWorkItemSchema.shape.number,
		owner: spaceTaskExternalWorkItemSchema.shape.owner,
		provider: spaceTaskExternalWorkItemProviderSchema,
		repo: spaceTaskExternalWorkItemSchema.shape.repo
	})
]);
const spaceTaskResponseSchema = z$1.strictObject({
	task: spaceTaskSchema,
	events: z$1.array(spaceTaskEventSchema)
});
const listSpaceTasksQuerySchema = z$1.strictObject({ phase: spaceTaskPhaseSchema.optional() });
const listSpaceTasksResponseSchema = z$1.strictObject({
	tasks: z$1.array(spaceTaskSchema),
	truncated: z$1.boolean()
});
const spaceTaskMutationReceiptSchema = z$1.strictObject({
	task: spaceTaskSchema,
	event: spaceTaskEventSchema,
	applied: z$1.boolean()
});
const SPACE_TASK_OPERATION_CONFLICT_ERROR_CODE = "space_task.operation_conflict";
const SPACE_TASK_REVISION_CONFLICT_ERROR_CODE = "space_task.revision_conflict";
const SPACE_TASK_EXTERNAL_WORK_ITEM_LIMIT_ERROR_CODE = "space_task.external_work_item_limit";
const SPACE_TASK_EXTERNAL_WORK_ITEM_NOT_FOUND_ERROR_CODE = "space_task.external_work_item_not_found";
const SPACE_TASK_ERROR_CODES = [
	SPACE_TASK_OPERATION_CONFLICT_ERROR_CODE,
	SPACE_TASK_REVISION_CONFLICT_ERROR_CODE,
	SPACE_TASK_EXTERNAL_WORK_ITEM_LIMIT_ERROR_CODE,
	SPACE_TASK_EXTERNAL_WORK_ITEM_NOT_FOUND_ERROR_CODE
];
const spaceTaskErrorCodeSchema = z$1.enum(SPACE_TASK_ERROR_CODES);
//#endregion
export { SPACE_TASK_COMPLETION_CONDITION_MAX_CHARS, SPACE_TASK_COMPLETION_CONDITION_MAX_ITEMS, SPACE_TASK_DESCRIPTION_MAX_CHARS, SPACE_TASK_ERROR_CODES, SPACE_TASK_EXTERNAL_WORK_ITEM_LIMIT_ERROR_CODE, SPACE_TASK_EXTERNAL_WORK_ITEM_MAX_ITEMS, SPACE_TASK_EXTERNAL_WORK_ITEM_NOT_FOUND_ERROR_CODE, SPACE_TASK_LIST_MAX_ITEMS, SPACE_TASK_OPERATION_CONFLICT_ERROR_CODE, SPACE_TASK_OPERATION_ID_MAX_CHARS, SPACE_TASK_RESULT_SUMMARY_MAX_CHARS, SPACE_TASK_REVIEW_FEEDBACK_MAX_CHARS, SPACE_TASK_REVISION_CONFLICT_ERROR_CODE, SPACE_TASK_TITLE_MAX_CHARS, createSpaceTaskBodySchema, describeLinkedGitHubIssueClosePrompt, formatSpaceTaskGitHubIssueLabel, listSpaceTasksQuerySchema, listSpaceTasksResponseSchema, mutateSpaceTaskBodySchema, spaceTaskActorSchema, spaceTaskErrorCodeSchema, spaceTaskEventSchema, spaceTaskEventTypeSchema, spaceTaskExternalWorkItemIdentityKey, spaceTaskExternalWorkItemObservedStateSchema, spaceTaskExternalWorkItemProviderSchema, spaceTaskExternalWorkItemSchema, spaceTaskGitHubIssueOperationId, spaceTaskIdSchema, spaceTaskMutationReceiptSchema, spaceTaskPhaseSchema, spaceTaskResponseSchema, spaceTaskResultSchema, spaceTaskReviewSchema, spaceTaskSchema };

//# sourceMappingURL=space-tasks.js.map